<script setup lang="ts">
import type { Component } from "vue";
import { computed, markRaw } from "vue";
import { useDialogLabel } from "../../composables/Dialog/useDialogLabel";
import { tryUseDialogLayer } from "../../composables/Dialog/useDialogLayer";
import VHelpTooltip from "../HelpTooltip/VHelpTooltip.vue";
import { closeReserveClass } from "./dialogSizes";

const {
  bare = false,
  compact = false,
  title = "",
  debug = undefined,
  reserveClose = true,
  help = "",
} = defineProps<{
  bare?: boolean
  compact?: boolean
  title?: string
  debug?: Component
  /** Keeps the title clear of the host close. Turn it off when a sibling action already sits under the close. */
  reserveClose?: boolean
  /** Help text behind a question mark after the title; never a line under it. */
  help?: string
}>();

defineSlots<{
  default?: () => unknown
}>();

const layer = tryUseDialogLayer();
const labelId = useDialogLabel("title");

const debugComponent = computed(() => debug ? markRaw(debug) : "span");

const rootClass = computed(() => {
  if (bare) {
    return undefined;
  }
  // The right padding keeps a long title clear of the host close (see closeReserveClass).
  const reserve = reserveClose ? closeReserveClass(Boolean(layer?.paddingless)) : null;
  if (layer?.paddingless) {
    return reserve ? `pl-5 ${reserve} pt-5 mb-5` : "px-5 pt-5 mb-5";
  }
  return reserve ? `${reserve} mb-5` : "mb-5";
});

const titleClass = computed(() => compact ? "text-base leading-5" : "text-xl leading-6");
</script>

<template>
  <div :class="rootClass">
    <component :is="debugComponent" v-bind="debug ? { inline: true } : {}">
      <strong :id="labelId" :class="titleClass">
        <slot>{{ title }}</slot>
      </strong>
    </component>
    <VHelpTooltip v-if="help" class="ml-1.5" :content="help" :subject="title" />
  </div>
</template>
